import sessionServices from '@/services/session-services';
import {
  type PayloadAction,
  createAsyncThunk,
  createSlice,
} from '@reduxjs/toolkit';
import type { RootState } from '../store';

// ========================================
// Types
// ========================================
interface SessionShareStatus {
  isShared: boolean;
  lastUpdated: number;
}

interface ShareState {
  // Map of sessionId -> share status
  sessions: Record<string, SessionShareStatus>;
  isLoading: Record<string, boolean>; // Per-session loading state
  error: Record<string, string | null>; // Per-session error state
}

// ========================================
// Share Status Caching Infrastructure
// ========================================
/**
 * In-memory cache for session share status to prevent redundant API calls
 * Key: sessionId
 * Value: { isShared, timestamp }
 *
 * Cache is automatically cleared on page refresh/navigation
 * TTL: 5 minutes (share status rarely changes)
 */
interface ShareStatusCacheEntry {
  isShared: boolean;
  timestamp: number;
}

const shareStatusCache = new Map<string, ShareStatusCacheEntry>();
const SHARE_STATUS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Invalidate cache for a specific session's share status
 * Call this when share/unshare actions occur
 */
function invalidateShareStatusCache(sessionId: string): void {
  shareStatusCache.delete(sessionId);
}

/**
 * Clear all cached share statuses
 * Call this on user logout
 */
function clearAllShareStatusCache(): void {
  shareStatusCache.clear();
}

// ========================================
// Initial State
// ========================================
const initialState: ShareState = {
  sessions: {},
  isLoading: {},
  error: {},
};

// ========================================
// Async Thunks
// ========================================

/**
 * Fetch share status for a session with caching
 * Returns cached data if available and fresh
 */
export const fetchShareStatus = createAsyncThunk(
  'share/fetchStatus',
  async (sessionId: string, { rejectWithValue }) => {
    try {
      // Check cache first
      const cached = shareStatusCache.get(sessionId);
      const now = Date.now();

      if (cached && now - cached.timestamp < SHARE_STATUS_CACHE_TTL) {
        return { sessionId, isShared: cached.isShared, fromCache: true };
      }

      const response = await sessionServices.getSharedSession(sessionId);

      if (response.status === 'success') {
        const isShared = response.data.is_shared;

        // Update cache
        shareStatusCache.set(sessionId, {
          isShared,
          timestamp: now,
        });

        return { sessionId, isShared, fromCache: false };
      } else {
        return rejectWithValue(
          response.message || 'Failed to fetch share status'
        );
      }
    } catch (error) {
      console.error('[fetchShareStatus] Error:', error);
      return rejectWithValue(
        error instanceof Error ? error.message : 'Unknown error'
      );
    }
  }
);

/**
 * Share a session (set is_shared to true)
 */
export const shareSession = createAsyncThunk(
  'share/shareSession',
  async ({ sessionId }: { sessionId: string }, { rejectWithValue }) => {
    try {
      const response = await sessionServices.shareSession({
        session_id: sessionId,
        is_shared: true,
      });

      if (response.status === 'success') {
        // Invalidate cache to force fresh fetch next time
        invalidateShareStatusCache(sessionId);
        return { sessionId, isShared: true };
      } else {
        return rejectWithValue(response.message || 'Failed to share session');
      }
    } catch (error) {
      console.error('[shareSession] Error:', error);
      return rejectWithValue(
        error instanceof Error ? error.message : 'Unknown error'
      );
    }
  }
);

/**
 * Unshare a session (set is_shared to false)
 */
export const unshareSession = createAsyncThunk(
  'share/unshareSession',
  async ({ sessionId }: { sessionId: string }, { rejectWithValue }) => {
    try {
      const response = await sessionServices.shareSession({
        session_id: sessionId,
        is_shared: false,
      });

      if (response.status === 'success') {
        // Invalidate cache to force fresh fetch next time
        invalidateShareStatusCache(sessionId);
        return { sessionId, isShared: false };
      } else {
        return rejectWithValue(response.message || 'Failed to unshare session');
      }
    } catch (error) {
      console.error('[unshareSession] Error:', error);
      return rejectWithValue(
        error instanceof Error ? error.message : 'Unknown error'
      );
    }
  }
);

// ========================================
// Slice
// ========================================
const shareSlice = createSlice({
  name: 'share',
  initialState,
  reducers: {
    // Clear share status for a specific session
    clearSessionShareStatus: (state, action: PayloadAction<string>) => {
      const sessionId = action.payload;
      delete state.sessions[sessionId];
      delete state.isLoading[sessionId];
      delete state.error[sessionId];
      invalidateShareStatusCache(sessionId);
    },
    // Clear all share data (e.g., on logout)
    clearAllShareData: (state) => {
      state.sessions = {};
      state.isLoading = {};
      state.error = {};
      clearAllShareStatusCache();
    },
  },
  extraReducers: (builder) => {
    // Fetch share status
    builder
      .addCase(fetchShareStatus.pending, (state, action) => {
        const sessionId = action.meta.arg;
        state.isLoading[sessionId] = true;
        state.error[sessionId] = null;
      })
      .addCase(fetchShareStatus.fulfilled, (state, action) => {
        const { sessionId, isShared } = action.payload;
        state.sessions[sessionId] = {
          isShared,
          lastUpdated: Date.now(),
        };
        state.isLoading[sessionId] = false;
        state.error[sessionId] = null;
      })
      .addCase(fetchShareStatus.rejected, (state, action) => {
        const sessionId = action.meta.arg;
        state.isLoading[sessionId] = false;
        state.error[sessionId] = action.payload as string;
      });

    // Share session
    builder
      .addCase(shareSession.pending, (state, action) => {
        const sessionId = action.meta.arg.sessionId;
        state.isLoading[sessionId] = true;
        state.error[sessionId] = null;
      })
      .addCase(shareSession.fulfilled, (state, action) => {
        const { sessionId, isShared } = action.payload;
        state.sessions[sessionId] = {
          isShared,
          lastUpdated: Date.now(),
        };
        state.isLoading[sessionId] = false;
        state.error[sessionId] = null;
      })
      .addCase(shareSession.rejected, (state, action) => {
        const sessionId = action.meta.arg.sessionId;
        state.isLoading[sessionId] = false;
        state.error[sessionId] = action.payload as string;
      });

    // Unshare session
    builder
      .addCase(unshareSession.pending, (state, action) => {
        const sessionId = action.meta.arg.sessionId;
        state.isLoading[sessionId] = true;
        state.error[sessionId] = null;
      })
      .addCase(unshareSession.fulfilled, (state, action) => {
        const { sessionId, isShared } = action.payload;
        state.sessions[sessionId] = {
          isShared,
          lastUpdated: Date.now(),
        };
        state.isLoading[sessionId] = false;
        state.error[sessionId] = null;
      })
      .addCase(unshareSession.rejected, (state, action) => {
        const sessionId = action.meta.arg.sessionId;
        state.isLoading[sessionId] = false;
        state.error[sessionId] = action.payload as string;
      });
  },
});

// ========================================
// Exports
// ========================================
export const {} = shareSlice.actions;

export const selectIsShared = (sessionId: string) => (state: RootState) =>
  state.share.sessions[sessionId]?.isShared ?? false;

export const selectShareLoading = (sessionId: string) => (state: RootState) =>
  state.share.isLoading[sessionId] ?? false;

export default shareSlice.reducer;
